import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { galleryImages } from '../data/mock';
import { getLocationBySlug } from '../data/locations';
import { brandInfo } from '../data/locations';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, Grid, ArrowLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { Button } from '../components/ui/button';

const GalleryPage = () => {
  const { slug } = useParams();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Get location data (default to Surabaya for legacy /gallery route)
  const location = slug ? getLocationBySlug(slug) : getLocationBySlug('surabaya');
  const schoolInfo = location?.schoolInfo || { name: brandInfo.name, logoUrl: brandInfo.logoUrl };
  
  // Only Surabaya has gallery images for now
  const images = location?.hasGallery ? galleryImages : [];
  const galleryTitle = location?.galleryTitle || { en: 'Gallery', id: 'Galeri' };

  // Update page title
  useEffect(() => {
    document.title = t(
      `Gallery - Studio Ballet Izora ${location?.city || ''} | ${galleryTitle.en}`,
      `Galeri - Studio Ballet Izora ${location?.city || ''} | ${galleryTitle.id}`
    );
  }, [language, t, location, galleryTitle]);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(images[index]);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex]);

  const backUrl = slug ? `/standorte/${slug}` : '/';

  // If no gallery available for this location
  if (!location?.hasGallery && slug) {
    return (
      <div className="min-h-screen bg-[#f7f5f2] flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold text-[#1e1919] mb-4">
            {t('Gallery Coming Soon', 'Galeri Segera Hadir')}
          </h1>
          <p className="text-[#736c64] mb-8">
            {t(
              `Gallery for ${location?.city || 'this location'} is coming soon!`,
              `Galeri untuk ${location?.city || 'lokasi ini'} akan segera hadir!`
            )}
          </p>
          <Button
            onClick={() => navigate(backUrl)}
            className="bg-[#8B1C1C] hover:bg-[#6d1616] text-white px-8 py-3 rounded-full font-semibold"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('Back to Studio', 'Kembali ke Studio')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back Button */}
            <button
              onClick={() => navigate(backUrl)}
              className="flex items-center gap-2 text-[#1e1919] hover:text-[#8B1C1C] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{t('Back', 'Kembali')}</span>
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img
                src={schoolInfo.logoUrl}
                alt={schoolInfo.name}
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="font-bold text-[#8B1C1C] hidden sm:block">
                {brandInfo.name}
              </span>
            </Link>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="text-sm font-medium text-[#736c64] hover:text-[#8B1C1C] transition-colors"
            >
              {language === 'en' ? 'ID' : 'EN'}
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-2 text-sm text-[#736c64]">
          <Link to="/" className="hover:text-[#8B1C1C] transition-colors">
            {t('Home', 'Beranda')}
          </Link>
          <ChevronRightIcon className="w-4 h-4" />
          {location && (
            <>
              <Link to={`/standorte/${location.slug}`} className="hover:text-[#8B1C1C] transition-colors">
                {location.city}
              </Link>
              <ChevronRightIcon className="w-4 h-4" />
            </>
          )}
          <span className="text-[#1e1919]">{t('Gallery', 'Galeri')}</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-4">
            {t(galleryTitle.en, galleryTitle.id)}
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-[#736c64] mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#8B1C1C]" />
              <span>{location?.city || 'Surabaya'}, {location?.country || 'Indonesia'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#8B1C1C]" />
              <span>2026</span>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 text-[#736c64]">
            <Grid className="w-4 h-4" />
            <span>{t(`${images.length} Photos`, `${images.length} Foto`)}</span>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative aspect-square overflow-hidden rounded-xl cursor-pointer group bg-white shadow-sm"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image}
                alt={`${t(galleryTitle.en, galleryTitle.id)} - ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#8B1C1C]/0 group-hover:bg-[#8B1C1C]/30 transition-colors duration-300 flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium text-lg">
                  {index + 1}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Back Button */}
        <div className="text-center mt-12">
          <Button
            onClick={() => navigate(backUrl)}
            variant="outline"
            className="border-[#8B1C1C] text-[#8B1C1C] hover:bg-[#8B1C1C] hover:text-white px-8 py-6 rounded-full font-semibold transition-all"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('Back to Studio', 'Kembali ke Studio')}
          </Button>
        </div>
      </main>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-50 p-2"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-2 sm:left-4 text-white/70 hover:text-white transition-colors z-50 p-2 bg-black/30 rounded-full"
          >
            <ChevronLeft className="w-8 h-8 sm:w-12 sm:h-12" />
          </button>

          {/* Image */}
          <div 
            className="max-w-5xl max-h-[85vh] px-4 sm:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt={`${t(galleryTitle.en, galleryTitle.id)} - ${currentIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-white/70 text-center mt-4">
              {currentIndex + 1} / {images.length}
            </p>
          </div>

          {/* Next Button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-2 sm:right-4 text-white/70 hover:text-white transition-colors z-50 p-2 bg-black/30 rounded-full"
          >
            <ChevronRight className="w-8 h-8 sm:w-12 sm:h-12" />
          </button>

          {/* Thumbnail dots */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-1 max-w-[90vw] overflow-x-auto py-2 px-4 bg-black/50 rounded-full">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                  setSelectedImage(images[idx]);
                }}
                className={`w-2 h-2 rounded-full transition-all flex-shrink-0 ${
                  idx === currentIndex ? 'bg-white w-4' : 'bg-white/40 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
